/**
 * Toolbar, panels, chips, toasts, perf overlay, loading + intro screens
 * (SPEC-11 §2/§3). The Setup panel is real (tier, overlay, sound, language,
 * device info); the other panels are labelled placeholders.
 */
import { t, translateDom, availableLocales, setLocale } from './i18n';
import type { Tier } from '@kota-nova/shared';

export interface UiHooks {
  onTierChange(tier: Tier): void;
  onOverlayChange(on: boolean): void;
  onSoundChange(on: boolean): void;
  onLanguageChange(code: string): void;
  deviceInfo(): string;
  statsLine(): string;
  onIntroEnter(): void;
  /** debug: tapping the perf HUD runs one shed step (P0-G08 on touch devices) */
  onForceShed(): void;
  /** Sky panel (P1): pin the world clock to hour 0..24 (null = follow server) */
  onSetTimeHours?(hour: number): void;
  onFollowClock?(): void;
  /** force a weather preset (null = back to the timeline) */
  onSetWeather?(kind: string | null): void;
  /** current clock hour + weather kind for panel initial state */
  skyState?(): { hour: number; weather: string; pinned: boolean };
}

// refreshDeviceInfo is returned by initUi so boot can update the Setup panel
// after tier detection finishes (the panel may have opened before detection).

const PANEL_TEXT: Record<string, string> = {
  play: 'panel.play',
  map: 'panel.map',
  sky: 'panel.sky',
  homes: 'panel.homes',
  avatar: 'panel.avatar',
  city: 'panel.city',
};

export function initUi(hooks: UiHooks): {
  refreshDeviceInfo(): void;
  setLoading(stepKey: string, pct: number): void;
  showIntro(): void;
  enterWorld(): void;
  toast(msg: string): void;
  setChips(district: string, clock: string, weather: string): void;
  setOverlayVisible(on: boolean): void;
  updateOverlay(): void;
  setTouchMode(touch: boolean): void;
  currentTier(): Tier | null;
} {
  const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
  const loading = $('loading');
  const loadingStep = $('loading-step');
  const loadingBar = $('loading-bar');
  const intro = $('intro');
  const hud = $('hud');
  const panel = $('panel');
  const perfHud = $('perf-hud');
  const touchControls = $('touch-controls');

  translateDom();

  let overlayOn = false;
  let overlayTimer = 0;
  let tierChoice: Tier | null = null;

  $('intro-enter').addEventListener('click', () => {
    intro.classList.add('hidden');
    hud.classList.remove('hidden');
    hooks.onIntroEnter();
    if (autoPanel) {
      const btn = document.querySelector<HTMLButtonElement>(`[data-panel="${autoPanel}"]`);
      if (btn) btn.click();
    }
  });

  // toolbar
  let openPanel: string | null = null;
  const autoPanel = new URLSearchParams(location.search).get('panel');
  document.querySelectorAll<HTMLButtonElement>('#toolbar .tool').forEach((btn) => {
    btn.addEventListener('click', () => {
      const name = btn.dataset['panel'] ?? '';
      if (openPanel === name) {
        closePanel();
        return;
      }
      openPanel = name;
      panel.classList.remove('hidden');
      if (name === 'setup') {
        renderSetupPanel();
      } else if (name === 'sky') {
        renderSkyPanel();
      } else {
        panel.innerHTML = `<h2>${escapeHtml(t(`tool.${name}`))}</h2><p class="muted">${escapeHtml(t(PANEL_TEXT[name] ?? 'setup.placeholderSoon'))}</p>`;
      }
    });
  });

  // Sky panel (P1 owner feedback): review day/night + weather without
  // waiting for the 30x world clock (a full day = 48 real minutes).
  function renderSkyPanel(): void {
    const state = hooks.skyState?.() ?? { hour: 12, weather: 'clear', pinned: false };
    const hours = [
      ['05:00', 'Dawn', 5.5],
      ['12:00', 'Noon', 12],
      ['18:30', 'Dusk', 18.5],
      ['22:00', 'Night', 22],
    ] as const;
    const weathers = ['clear', 'cloudy', 'rain', 'storm', 'fog', 'snow'] as const;
    panel.innerHTML = `
      <h2>${escapeHtml(t('tool.sky'))}</h2>
      <label>${escapeHtml('Time')}
        <input type="range" id="sky-hour" min="0" max="23.9" step="0.1" value="${state.hour.toFixed(1)}" />
      </label>
      <div class="muted" id="sky-hour-label" style="font-size:12px">${state.pinned ? 'pinned' : 'following city clock'} · <span id="sky-hour-val"></span></div>
      <div class="sky-row">
        ${hours.map(([, name, h]) => `<button class="tool" data-hour="${String(h)}">${name}</button>`).join('')}
        <button class="tool" data-follow>Follow clock</button>
      </div>
      <label>${escapeHtml('Weather')}</label>
      <div class="sky-row">
        ${weathers
          .map(
            (w) =>
              `<button class="tool ${w === state.weather ? 'active' : ''}" data-weather="${w}">${w[0]?.toUpperCase() ?? ''}${w.slice(1)}</button>`,
          )
          .join('')}
        <button class="tool" data-weather="">Auto (timeline)</button>
      </div>
      <div class="muted" style="font-size:12px">Drag the slider to pin the sun; “Follow clock” hands it back. “Auto” returns weather to the timeline.</div>
    `;
    const fmt = (h: number): string =>
      `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.floor((h % 1) * 60)).padStart(2, '0')}`;
    const slider = panel.querySelector<HTMLInputElement>('#sky-hour') as HTMLInputElement;
    const val = panel.querySelector<HTMLElement>('#sky-hour-val') as HTMLElement;
    const sync = (): void => {
      val.textContent = fmt(Number(slider.value));
    };
    sync();
    slider.addEventListener('input', () => {
      sync();
      hooks.onSetTimeHours?.(Number(slider.value));
    });
    panel.querySelectorAll<HTMLElement>('[data-hour]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const h = Number(btn.dataset['hour'] ?? '12');
        slider.value = String(h);
        sync();
        hooks.onSetTimeHours?.(h);
      });
    });
    panel.querySelector<HTMLElement>('[data-follow]')?.addEventListener('click', () => {
      hooks.onFollowClock?.();
      const s = hooks.skyState?.();
      if (s) {
        slider.value = s.hour.toFixed(1);
        sync();
      }
    });
    panel.querySelectorAll<HTMLElement>('[data-weather]').forEach((btn) => {
      btn.addEventListener('click', () => {
        panel.querySelectorAll<HTMLElement>('.tool').forEach((b) => {
          if (b.dataset['weather'] !== undefined) b.classList.remove('active');
        });
        btn.classList.add('active');
        const k = btn.dataset['weather'];
        hooks.onSetWeather?.(k ? k : null);
      });
    });
  }

  function closePanel(): void {
    openPanel = null;
    panel.classList.add('hidden');
  }

  function renderSetupPanel(): void {
    const saved = safeGet('kn_tier');
    const overlay = safeGet('kn_overlay') === '1';
    const sound = safeGet('kn_sound') !== '0';
    panel.innerHTML = `
      <h2>${escapeHtml(t('tool.setup'))}</h2>
      <label>${escapeHtml(t('setup.tier'))}
        <select id="set-tier">
          ${['low', 'mid', 'high']
            .map(
              (x) =>
                `<option value="${x}" ${x === (tierChoice ?? saved) ? 'selected' : ''}>${escapeHtml(t(`tier.${x}`))}</option>`,
            )
            .join('')}
        </select>
      </label>
      <div class="muted" style="font-size:12px">${escapeHtml(t('setup.tierHint'))}</div>
      <label>${escapeHtml(t('setup.perfOverlay'))}
        <input type="checkbox" id="set-overlay" ${overlay ? 'checked' : ''} />
      </label>
      <label>${escapeHtml(t('setup.sound'))}
        <input type="checkbox" id="set-sound" ${sound ? 'checked' : ''} />
      </label>
      <label>${escapeHtml(t('setup.language'))}
        <select id="set-lang">
          ${availableLocales()
            .map(
              (l) =>
                `<option value="${l.code}" ${l.code === 'en' ? 'selected' : ''}>${escapeHtml(l.label)}</option>`,
            )
            .join('')}
        </select>
      </label>
      <label>${escapeHtml(t('setup.controls'))}
        <span class="muted">WASD / arrows · Shift run · drag to look</span>
      </label>
      <div class="device-info" id="set-device"></div>
    `;
    $('set-device').textContent = hooks.deviceInfo();
    $('set-tier').addEventListener('change', (e) => {
      const v = (e.target as HTMLSelectElement).value as Tier;
      tierChoice = v;
      safeSet('kn_tier', v);
      hooks.onTierChange(v);
    });
    $('set-overlay').addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      safeSet('kn_overlay', on ? '1' : '0');
      overlayOn = on;
      setOverlayVisible(on);
      hooks.onOverlayChange(on);
    });
    $('set-sound').addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      safeSet('kn_sound', on ? '1' : '0');
      hooks.onSoundChange(on);
    });
    $('set-lang').addEventListener('change', (e) => {
      setLocale((e.target as HTMLSelectElement).value);
      hooks.onLanguageChange((e.target as HTMLSelectElement).value);
      translateDom();
    });
    overlayOn = overlay;
  }
  // ?panel=setup (tests): open that panel once the HUD becomes visible
  if (autoPanel) {
    const open = (): void => {
      const btn = document.querySelector<HTMLButtonElement>(`[data-panel="${autoPanel}"]`);
      if (btn) btn.click();
    };
    setTimeout(open, 0);
    setTimeout(open, 400); // once more after intro-enter
  }

  // perf overlay poll (1 Hz is enough for the readout)
  perfHud.addEventListener('pointerdown', () => hooks.onForceShed());
  function setOverlayVisible(on: boolean): void {
    perfHud.classList.toggle('hidden', !on);
    if (on && !overlayTimer) {
      overlayTimer = window.setInterval(() => {
        perfHud.textContent = hooks.statsLine();
      }, 500);
    } else if (!on && overlayTimer) {
      clearInterval(overlayTimer);
      overlayTimer = 0;
    }
  }
  if (safeGet('kn_overlay') === '1') setOverlayVisible(true);

  return {
    refreshDeviceInfo() {
      const el = document.getElementById('set-device');
      if (el) el.textContent = hooks.deviceInfo();
    },
    setLoading(stepKey, pct) {
      loading.classList.remove('hidden');
      loadingStep.textContent = t(stepKey);
      loadingBar.style.width = `${Math.round(pct * 100)}%`;
    },
    showIntro() {
      loading.classList.add('hidden');
      intro.classList.remove('hidden');
    },
    enterWorld() {
      hud.classList.remove('hidden');
    },
    toast(msg) {
      const host = $('toast-host');
      const el = document.createElement('div');
      el.className = 'toast';
      el.textContent = msg;
      host.appendChild(el);
      setTimeout(() => el.remove(), 2600);
    },
    setChips(district, clock, weather) {
      $('chip-district').textContent = district;
      $('chip-clock').textContent = clock;
      $('chip-weather').textContent = weather;
    },
    setOverlayVisible,
    updateOverlay() {
      if (overlayOn) perfHud.textContent = hooks.statsLine();
    },
    setTouchMode(touch) {
      touchControls.classList.toggle('hidden', !touch);
    },
    currentTier: () => tierChoice,
  };
}

function safeGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* private mode */
  }
}
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}
